import React, { useState } from "react";
import Layout from "./Layout";
import { FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { changePassword } from "../services/api";
import ProfileSidebar from "./ProfileSidebar";
import { useAuthStore } from "../store/authStore";

export default function ChangePassword() {
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.accessToken);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setMessage("");

    // -------------------------
    // VALIDATION
    // -------------------------

    if (!currentPassword) {
      setError("Current password is required");
      return;
    }

    if (!newPassword) {
      setError("New password is required");
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }

    if (!confirmPassword) {
      setError("Please confirm your new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match");
      return;
    }

    try {
      setLoading(true);

      const res = await changePassword({
        userId: user.id,
        currentPassword,
        newPassword,
        token,
      });

      if (res.success) {
        setMessage(res.message);

        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setError(res.message || "Failed to change password");
      }
    } catch (err) {
      console.error("Change password error:", err);

      if (err.response?.status === 401) {
        setError("Current password is incorrect");
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to change password. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="bg-[#FFF8EC] px-6 md:px-16 py-8">
        <div className=" mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center">
              <FaLock className="text-2xl text-[#FF945B]" />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-light_primary">
                Change Password
              </h2>

              <p className="text-gray-600 mt-1">
                Update your account password securely
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 md:px-12 sm:px-12 xs:px-10 xxs:px-8 py-6 md:py-10 lg:py-10 sm:py-10 xs:py-8 xxs:py-6 mx-auto bg-white">
        <div className="mx-auto grid grid-cols-1 md:grid-cols-[270px_1fr] gap-8">
          {/* REUSABLE SIDEBAR */}
          <ProfileSidebar />

          {/* =========================
                    PROFILE CARD
                ========================= */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
            <h3 className="text-2xl font-semibold text-gray-900 border-b border-gray-200 pb-5 mb-6">
              Update Password
            </h3>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>

                <div className="relative">
                  <input
                    type={showCurrent ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 pr-12 outline-none focus:border-[#B88A3B]"
                  />

                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#E87932]"
                  >
                    {showCurrent ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>

                <div className="relative">
                  <input
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 pr-12 outline-none focus:border-[#B88A3B]"
                  />

                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#E87932]"
                  >
                    {showNew ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Password must be at least 6 characters.
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>

                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 pr-12 outline-none focus:border-[#B88A3B]"
                  />

                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#E87932]"
                  >
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Success */}
              {message && (
                <div className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                  {message}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-light_primary text-white py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-60 transition"
              >
                {loading ? "Changing Password..." : "Change Password"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
